import * as React from 'react'

import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import type { SxProps, Theme } from '@mui/material/styles'

import { KnockStatusChip } from '@user/features/MapView/KnockStatusChip'
import { useD2DKnockEnabled } from '@user/store/tenantFeatures/hooks/useD2DKnockEnabled'
import { useMapViewFeatureEnabled } from '@user/store/tenantFeatures/hooks/useMapViewFeatureEnabled'

import type { MostRecentKnockStatusValue } from './helpers'
import Stack from '@mui/material/Stack'

type Props = {
  knockStatus: MostRecentKnockStatusValue | null
  /** `detail` renders an outlined Paper box; `card` renders a top-of-card strip. */
  variant?: 'detail' | 'card'
  sx?: SxProps<Theme>
}

export function MostRecentKnockStatus(props: Props) {
  const { knockStatus, variant = 'detail', sx } = props

  const mapViewEnabled = useMapViewFeatureEnabled()
  const d2dKnockEnabled = useD2DKnockEnabled()

  // Hidden unless both feature flags are on and there is a knock status to show.
  if (!mapViewEnabled || !d2dKnockEnabled || !knockStatus) {
    return null
  }

  const content = (
    <Typography
      variant="body2"
      sx={{
        mt: '4px',
        display: '-webkit-box',
        WebkitLineClamp: variant === 'card' ? 2 : 4,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}
    >
      <Typography component="span" variant="subtitle2">
        Reason:{' '}
      </Typography>
      {knockStatus.reason ?? '-'}
    </Typography>
  )

  if (variant === 'card') {
    return (
      <Box
        data-test-id="most-recent-knock-status"
        sx={{
          px: 1.5,
          py: 1,
          border: 'solid 1px #d1d1d6',
          borderRadius: 1,
          backgroundColor: 'common.white',
          boxShadow: '0 2px 2px 0 #dddddd',
          ...sx,
        }}
      >
        <Stack display="flex" direction="row" gap="9px" alignItems="center">
          <Typography
            variant="caption"
            sx={{
              fontWeight: 400,
              letterSpacing: '0.4px',
              fontSize: '14px',
            }}
          >
            Current knock status
          </Typography>

          <KnockStatusChip
            status={knockStatus.status}
            isDefault={knockStatus.isDefault}
          />
        </Stack>
        {content}
      </Box>
    )
  }

  return (
    <Paper
      data-test-id="most-recent-knock-status"
      elevation={0}
      variant="outlined"
      sx={{
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        ...sx,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 400,
          letterSpacing: '0.4px',
          fontSize: '14px',
        }}
      >
        Current knock status
      </Typography>

      <Box sx={{ mt: 0.6 }}>
        <KnockStatusChip
          status={knockStatus.status}
          isDefault={knockStatus.isDefault}
        />
      </Box>

      {content}
    </Paper>
  )
}

MostRecentKnockStatus.displayName = 'MostRecentKnockStatus'
